import type { MobileRunState } from "./mobileRunState"

export interface PublisherClaim {
    channelId: string
    claim: string
    claimExpiresAt: string
}

export type PublisherResult =
    | { status: "published"; publication: "applied" | "resumed"; requested: number; succeeded: number; failed: number }
    | { status: "unavailable"; reason: "channel-not-created" | "not-electron" }

export interface PublisherDiagnostics {
    status: "idle" | "healthy" | "retrying" | "degraded" | "stale"
    queueDepth: number
    lastNodeEventSequence: number | null
    lastAcceptedGatewaySequence: number | null
    lastNodeEventAt: string | null
    lastAttemptAt: string | null
    lastAcceptedAt: string | null
    retryState: { status: "idle" | "sending" | "retrying" | "failed"; attempt: number }
    lastErrorCode: string | null
    partialFanout: null | {
        requested: number
        succeeded: number
        failed: number
        errors: Array<{ code: string; retryable: boolean }>
    }
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value)
}

function requiredString(value: unknown, field: string): string {
    if (typeof value !== "string" || value.trim().length === 0) throw new Error(`Push publisher response is missing ${field}`)
    return value
}

function nonNegativeInteger(value: unknown, field: string): number {
    if (typeof value !== "number" || !Number.isSafeInteger(value) || value < 0) throw new Error(`Push publisher response has invalid ${field}`)
    return value
}

export async function createPublisherClaim(nodeId: string): Promise<PublisherClaim | null> {
    const api = window.wildlarkAPI?.pushPublisher
    if (!api) return null
    const value: unknown = await api.createClaim(nodeId)
    if (!isRecord(value)) throw new Error("Push publisher returned an invalid claim")
    const claimExpiresAt = requiredString(value.claimExpiresAt, "claimExpiresAt")
    if (!Number.isFinite(Date.parse(claimExpiresAt))) throw new Error("Push publisher returned an invalid claim expiry")
    return {
        channelId: requiredString(value.channelId, "channelId"),
        claim: requiredString(value.claim, "claim"),
        claimExpiresAt,
    }
}

export async function publishMobileRunState(state: MobileRunState): Promise<PublisherResult> {
    const api = window.wildlarkAPI?.pushPublisher
    if (!api) return { status: "unavailable", reason: "not-electron" }
    const value: unknown = await api.publish(state)
    if (!isRecord(value)) throw new Error("Push publisher returned an invalid publication result")
    if (value.status === "unavailable") {
        if (value.reason !== "channel-not-created") throw new Error("Push publisher returned an unknown unavailable reason")
        return { status: "unavailable", reason: value.reason }
    }
    if (value.status !== "published" || (value.publication !== "applied" && value.publication !== "resumed") || !isRecord(value.fanout)) {
        throw new Error("Push publisher returned an invalid publication result")
    }
    const requested = nonNegativeInteger(value.fanout.requested, "requested")
    const succeeded = nonNegativeInteger(value.fanout.succeeded, "succeeded")
    const failed = nonNegativeInteger(value.fanout.failed, "failed")
    if (requested !== succeeded + failed) throw new Error("Push publisher returned inconsistent fan-out counters")
    return { status: "published", publication: value.publication, requested, succeeded, failed }
}

export async function getPublisherDiagnostics(): Promise<PublisherDiagnostics | null> {
    const api = window.wildlarkAPI?.pushPublisher
    if (!api) return null
    return parsePublisherDiagnostics(await api.diagnostics())
}

export function parsePublisherDiagnostics(value: unknown): PublisherDiagnostics {
    if (!isRecord(value)) throw new Error("Push publisher diagnostics are invalid")
    const status = value.status
    if (status !== "idle" && status !== "healthy" && status !== "retrying" && status !== "degraded" && status !== "stale") {
        throw new Error("Push publisher diagnostic status is invalid")
    }
    if (!isRecord(value.retryState)) throw new Error("Push publisher retry state is invalid")
    const retryStatus = value.retryState.status
    if (retryStatus !== "idle" && retryStatus !== "sending" && retryStatus !== "retrying" && retryStatus !== "failed") {
        throw new Error("Push publisher retry status is invalid")
    }
    return {
        status,
        queueDepth: nonNegativeInteger(value.queueDepth, "queueDepth"),
        lastNodeEventSequence: optionalNonNegativeInteger(value.lastNodeEventSequence, "lastNodeEventSequence"),
        lastAcceptedGatewaySequence: optionalNonNegativeInteger(value.lastAcceptedGatewaySequence, "lastAcceptedGatewaySequence"),
        lastNodeEventAt: optionalTimestamp(value.lastNodeEventAt, "lastNodeEventAt"),
        lastAttemptAt: optionalTimestamp(value.lastAttemptAt, "lastAttemptAt"),
        lastAcceptedAt: optionalTimestamp(value.lastAcceptedAt, "lastAcceptedAt"),
        retryState: { status: retryStatus, attempt: nonNegativeInteger(value.retryState.attempt, "retry attempt") },
        lastErrorCode: optionalIdentifier(value.lastErrorCode, "lastErrorCode"),
        partialFanout: parseDiagnosticFanout(value.partialFanout),
    }
}

function parseDiagnosticFanout(value: unknown): PublisherDiagnostics["partialFanout"] {
    if (value === null) return null
    if (!isRecord(value) || !Array.isArray(value.errors)) throw new Error("Push publisher partial fan-out is invalid")
    const requested = nonNegativeInteger(value.requested, "partial requested")
    const succeeded = nonNegativeInteger(value.succeeded, "partial succeeded")
    const failed = nonNegativeInteger(value.failed, "partial failed")
    if (requested !== succeeded + failed || failed === 0 || value.errors.length > 5) throw new Error("Push publisher partial fan-out is inconsistent")
    return {
        requested,
        succeeded,
        failed,
        errors: value.errors.map((item) => {
            if (!isRecord(item) || typeof item.retryable !== "boolean") throw new Error("Push publisher partial error is invalid")
            return { code: requiredString(item.code, "partial error code"), retryable: item.retryable }
        }),
    }
}

function optionalNonNegativeInteger(value: unknown, field: string): number | null {
    return value === null ? null : nonNegativeInteger(value, field)
}

function optionalTimestamp(value: unknown, field: string): string | null {
    if (value === null) return null
    const timestamp = requiredString(value, field)
    if (!Number.isFinite(Date.parse(timestamp))) throw new Error(`Push publisher response has invalid ${field}`)
    return timestamp
}

function optionalIdentifier(value: unknown, field: string): string | null {
    if (value === null) return null
    const identifier = requiredString(value, field)
    if (identifier.length > 160) throw new Error(`Push publisher response has invalid ${field}`)
    return identifier
}

export async function revokePublisherDevice(
    nodeId: string,
    deviceRef: string
): Promise<{ status: "revoked" | "already-revoked" | "not-enrolled" | "not-electron" }> {
    const api = window.wildlarkAPI?.pushPublisher
    if (!api) return { status: "not-electron" }
    const value: unknown = await api.revokeDevice(nodeId, deviceRef)
    const source = isRecord(value) ? value : null
    if (!source || (source.status !== "revoked" && source.status !== "already-revoked" && source.status !== "not-enrolled")) {
        throw new Error("Push publisher returned an invalid device revocation result")
    }
    return { status: source.status }
}
